import { t, useLanguage } from "../../i18n";
import { useState, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentRequestButtonElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { paymentAPI } from '../../services/api';
import { Button } from '../../components/ui/button';
import { CreditCard, Loader2, Apple, Smartphone } from 'lucide-react';
import { toast } from 'sonner';

// Get Stripe publishable key from environment or default
const STRIPE_KEY = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || 'pk_test_51T9AYALgaugfXf27s1QXfwEMWZtxNd9HvEqURJnPRXNe0aozir355IBendiHS2MTQGbLH0ARwGC1niFYlnkftwTN00mFuOLdop';
const stripePromise = loadStripe(STRIPE_KEY);

// Express Payment Button Component
const ExpressPaymentButton = ({
  orderId,
  amount,
  onSuccess,
  onCancel,
  label = t("Payer")
}) => {
  useLanguage();
  const stripe = useStripe();
  const [paymentRequest, setPaymentRequest] = useState(null);
  const [canMakePayment, setCanMakePayment] = useState(false);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    if (!stripe || !amount) return;
    const pr = stripe.paymentRequest({
      country: 'FR',
      currency: 'eur',
      total: {
        label: label,
        amount: Math.round(amount * 100) // Convert to cents
      },
      requestPayerName: false,
      requestPayerEmail: false
    });

    // Check if the Payment Request is available
    pr.canMakePayment().then(result => {
      if (result) {
        setPaymentRequest(pr);
        setCanMakePayment(true);
      }
      setLoading(false);
    });

    // Handle payment method
    pr.on('paymentmethod', async ev => {
      try {
        // Create payment intent
        const intentResponse = await paymentAPI.createPaymentIntent(orderId);
        const {
          client_secret
        } = intentResponse.data;

        // Confirm payment
        const {
          error: confirmError,
          paymentIntent
        } = await stripe.confirmCardPayment(client_secret, {
          payment_method: ev.paymentMethod.id
        }, {
          handleActions: false
        });
        if (confirmError) {
          ev.complete('fail');
          toast.error(t("Paiement échoué"));
          return;
        }
        ev.complete('success');
        if (paymentIntent.status === 'requires_action') {
          const {
            error
          } = await stripe.confirmCardPayment(client_secret);
          if (error) {
            toast.error(t("Paiement échoué"));
            return;
          }
        }

        // Confirm on backend
        await paymentAPI.confirmIntent(paymentIntent.id);
        toast.success(t("Paiement réussi !"));
        onSuccess?.();
      } catch (err) {
        ev.complete('fail');
        toast.error(t("Erreur de paiement"));
        console.error('Payment error:', err);
      }
    });
    pr.on('cancel', () => {
      onCancel?.();
    });
  }, [stripe, amount, orderId, label, onSuccess, onCancel]);
  if (loading) {
    return <div className="flex items-center justify-center py-3">
        <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
      </div>;
  }
  if (!canMakePayment) {
    return null; // Don't show if Apple Pay/Google Pay not available
  }
  return <div className="w-full">
      <PaymentRequestButtonElement options={{
      paymentRequest,
      style: {
        paymentRequestButton: {
          type: 'default',
          theme: 'dark',
          height: '56px'
        }
      }
    }} />

    </div>;
};

// Express Tip Payment Button Component
const ExpressTipButton = ({
  orderId,
  amount,
  onSuccess,
  onCancel
}) => {
  useLanguage();
  const stripe = useStripe();
  const [paymentRequest, setPaymentRequest] = useState(null);
  const [canMakePayment, setCanMakePayment] = useState(false);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    if (!stripe || !amount || amount <= 0) return;
    const pr = stripe.paymentRequest({
      country: 'FR',
      currency: 'eur',
      total: {
        label: t("Pourboire"),
        amount: Math.round(amount * 100)
      },
      requestPayerName: false,
      requestPayerEmail: false
    });
    pr.canMakePayment().then(result => {
      if (result) {
        setPaymentRequest(pr);
        setCanMakePayment(true);
      }
      setLoading(false);
    });
    pr.on('paymentmethod', async ev => {
      try {
        const intentResponse = await paymentAPI.createTipIntent(orderId, amount);
        const {
          client_secret
        } = intentResponse.data;
        const {
          error: confirmError,
          paymentIntent
        } = await stripe.confirmCardPayment(client_secret, {
          payment_method: ev.paymentMethod.id
        }, {
          handleActions: false
        });
        if (confirmError) {
          ev.complete('fail');
          toast.error(t("Paiement échoué"));
          return;
        }
        ev.complete('success');
        if (paymentIntent.status === 'requires_action') {
          const {
            error
          } = await stripe.confirmCardPayment(client_secret);
          if (error) {
            toast.error(t("Paiement échoué"));
            return;
          }
        }
        await paymentAPI.confirmIntent(paymentIntent.id);
        toast.success(t("Merci pour votre pourboire !"));
        onSuccess?.();
      } catch (err) {
        ev.complete('fail');
        toast.error(t("Erreur de paiement"));
      }
    });
    pr.on('cancel', () => {
      onCancel?.();
    });
  }, [stripe, amount, orderId, onSuccess, onCancel]);

  // Update payment request amount when it changes
  useEffect(() => {
    if (paymentRequest && amount > 0) {
      paymentRequest.update({
        total: {
          label: t("Pourboire"),
          amount: Math.round(amount * 100)
        }
      });
    }
  }, [paymentRequest, amount]);
  if (loading) {
    return null;
  }
  if (!canMakePayment) {
    return null;
  }
  return <div className="w-full">
      <PaymentRequestButtonElement options={{
      paymentRequest,
      style: {
        paymentRequestButton: {
          type: 'default',
          theme: 'dark',
          height: '56px'
        }
      }
    }} />

    </div>;
};

// Wrapper components with Stripe Elements
export const ExpressCheckout = ({
  orderId,
  amount,
  onSuccess,
  onCancel,
  label
}) => {
  useLanguage();
  return <Elements key={useLanguage.getState().language} stripe={stripePromise} options={{
    locale: useLanguage.getState().language
  }}>
      <ExpressPaymentButton orderId={orderId} amount={amount} onSuccess={onSuccess} onCancel={onCancel} label={label} />

    </Elements>;
};
export const ExpressTipPayment = ({
  orderId,
  amount,
  onSuccess,
  onCancel
}) => {
  useLanguage();
  if (!amount || amount <= 0) return null;
  return <Elements key={useLanguage.getState().language} stripe={stripePromise} options={{
    locale: useLanguage.getState().language
  }}>
      <ExpressTipButton orderId={orderId} amount={amount} onSuccess={onSuccess} onCancel={onCancel} />

    </Elements>;
};

// Payment methods info component
export const PaymentMethodsInfo = () => {
  useLanguage();
  return <div className="flex items-center justify-center gap-3 text-gray-400 text-xs">
      <Apple className="w-5 h-5" />
      <span>Apple Pay</span>
      <span className="text-gray-300">•</span>
      <Smartphone className="w-5 h-5" />
      <span>Google Pay</span>
      <span className="text-gray-300">•</span>
      <CreditCard className="w-4 h-4" />
      <span>{t("Carte")}</span>
    </div>;
};
export default ExpressCheckout;
